import React, { createContext, useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SiteSettingsContext = createContext({});

const DEFAULT_LOGO = 'https://customer-assets.emergentagent.com/job_instant-access-space/artifacts/jtfp03jx_Flexic.jpg';
const DEFAULT_HERO = 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=1200&q=80';
const DEFAULT_AUTH_BG = 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&q=80';

// Default marquee items in 3 languages. An item is an object {fr, nl, en}.
// Legacy string items (plain FR strings) are still accepted and auto-upgraded on read.
const DEFAULT_USE_CASES = [
  { fr: "Pour une formation", nl: "Voor een opleiding", en: "For a training" },
  { fr: "Pour une seance photo", nl: "Voor een fotoshoot", en: "For a photo shoot" },
  { fr: "Pour une reunion de syndic", nl: "Voor een syndicusvergadering", en: "For a condo meeting" },
  { fr: "Pour un atelier de reparation", nl: "Voor een reparatieworkshop", en: "For a repair workshop" },
  { fr: "Pour une presentation de produit", nl: "Voor een productpresentatie", en: "For a product launch" },
  { fr: "Pour une seance de degustation", nl: "Voor een degustatiesessie", en: "For a tasting session" },
  { fr: "Pour un club d'investissement", nl: "Voor een investeringsclub", en: "For an investment club" },
  { fr: "Pour un cours de yoga", nl: "Voor een yogales", en: "For a yoga class" },
  { fr: "Pour un coaching individuel", nl: "Voor een individuele coaching", en: "For one-on-one coaching" },
  { fr: "Pour un afterwork networking", nl: "Voor een afterwork-netwerkevent", en: "For an afterwork networking" },
];

// Default highlights: site's strong points, displayed as bullets below the hero.
const DEFAULT_HIGHLIGHTS = [
  { fr: "Acces facile en voiture", nl: "Vlot bereikbaar met de auto", en: "Easy access by car" },
  { fr: "Parking disque bleu jusqu'a 18h", nl: "Blauwe zone tot 18u", en: "Blue-zone parking until 6 pm" },
  { fr: "Terminus des bus 28 et 36 a 2 minutes", nl: "Eindhalte bus 28 en 36 op 2 min", en: "Bus 28 & 36 terminus 2 min away" },
  { fr: "Acces 7j/7 par code personnel", nl: "Toegang 7/7 met persoonlijke code", en: "7/7 access by personal code" },
  { fr: "Cafe, the et eau a volonte", nl: "Koffie, thee en water onbeperkt", en: "Free coffee, tea & water" },
];

const DEFAULT_HIGHLIGHTS_TITLE = {
  fr: "Pourquoi choisir Flexi-location ?",
  nl: "Waarom kiezen voor Flexi-location?",
  en: "Why choose Flexi-location?",
};

// Default important infos / announcements (opening prices, works, etc.).
const DEFAULT_INFOS = [
  {
    fr: "Tarifs d'ouverture - susceptibles d'evoluer dans les prochains mois.",
    nl: "Openingstarieven - kunnen de komende maanden evolueren.",
    en: "Opening prices - subject to change in the coming months.",
  },
];

const DEFAULT_INFOS_TITLE = {
  fr: "Infos importantes",
  nl: "Belangrijke info",
  en: "Important info",
};

/** Normalize one item to `{fr, nl, en}` whether it was saved as string or object. */
export function normalizeUseCase(item) {
  if (!item) return { fr: '', nl: '', en: '' };
  if (typeof item === 'string') return { fr: item, nl: '', en: '' };
  return {
    fr: item.fr || '',
    nl: item.nl || '',
    en: item.en || '',
  };
}

/** Normalize an info item: same as use-case + optional `expires_at` (YYYY-MM-DD). */
export function normalizeInfo(item) {
  const base = normalizeUseCase(item);
  const exp = (item && typeof item === 'object' && item.expires_at) ? String(item.expires_at).trim() : '';
  return { ...base, expires_at: exp };
}

/** Returns true if the info is still active today (no expiration, or expiration >= today YYYY-MM-DD). */
export function isInfoActive(info) {
  if (!info || !info.expires_at) return true;
  // Compare as YYYY-MM-DD (lexical compare works because of zero-padding).
  const today = new Date().toISOString().slice(0, 10);
  return info.expires_at >= today;
}

/** Pick the string for the given language, falling back to FR. */
export function pickUseCaseText(item, lang) {
  const n = normalizeUseCase(item);
  const l = (lang || 'fr').slice(0, 2).toLowerCase();
  if (l === 'nl' && n.nl) return n.nl;
  if (l === 'en' && n.en) return n.en;
  return n.fr;
}

export function SiteSettingsProvider({ children }) {
  const { i18n } = useTranslation();
  const [settings, setSettings] = useState({ logo_url: '', hero_url: '', auth_bg_url: '' });

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/site-settings`);
        setSettings(res.data);
      } catch { /* ignore */ }
    };
    fetch();
  }, []);

  // Apply the admin-selected color theme by setting data-theme on <html>. The
  // overrides live in /src/themes.css. Falling back to "default" (= no overrides)
  // keeps the current palette when no theme is picked.
  useEffect(() => {
    const theme = settings.theme || 'default';
    document.documentElement.setAttribute('data-theme', theme);
  }, [settings.theme]);

  const refresh = async () => {
    try {
      const res = await axios.get(`${API}/site-settings`);
      setSettings(res.data);
    } catch { /* ignore */ }
  };

  const logo = settings.logo_url || DEFAULT_LOGO;
  const hero = settings.hero_url || DEFAULT_HERO;
  const authBg = settings.auth_bg_url || DEFAULT_AUTH_BG;

  // Raw items (array of either strings or {fr,nl,en} objects)
  const rawUseCases = (settings.use_cases && settings.use_cases.length > 0)
    ? settings.use_cases
    : DEFAULT_USE_CASES;

  // Localized plain-string array for the marquee — reacts to language change.
  const useCases = rawUseCases.map(item => pickUseCaseText(item, i18n.language));

  const marqueeSpeed1 = settings.marquee_speed_1 || 35;
  const marqueeSpeed2 = settings.marquee_speed_2 || 28;
  // Direction: "ltr" = items move right-to-left (default), "rtl" = items move left-to-right.
  const marqueeDir1 = settings.marquee_dir_1 || "ltr";
  const marqueeDir2 = settings.marquee_dir_2 || "ltr";

  // Highlights (raw + localized). Strong points displayed below hero on the home page.
  const rawHighlights = (settings.highlights && settings.highlights.length > 0)
    ? settings.highlights
    : DEFAULT_HIGHLIGHTS;
  const highlights = rawHighlights.map(item => pickUseCaseText(item, i18n.language));

  const rawHighlightsTitle = settings.highlights_title || DEFAULT_HIGHLIGHTS_TITLE;
  const highlightsTitle = pickUseCaseText(rawHighlightsTitle, i18n.language);

  // Important infos / announcements (filter out expired ones for public display).
  const rawInfos = (settings.infos && settings.infos.length > 0)
    ? settings.infos
    : DEFAULT_INFOS;
  const activeInfosRaw = rawInfos.filter(isInfoActive);
  const infos = activeInfosRaw.map(item => pickUseCaseText(item, i18n.language));

  const rawInfosTitle = settings.infos_title || DEFAULT_INFOS_TITLE;
  const infosTitle = pickUseCaseText(rawInfosTitle, i18n.language);

  // Hero overlay opacity (0..100). Controls the white scrim on top of the hero
  // image so the black title stays readable. Default 50 = balanced.
  const heroOverlayOpacity = (typeof settings.hero_overlay_opacity === 'number')
    ? Math.max(0, Math.min(100, settings.hero_overlay_opacity))
    : 50;

  return (
    <SiteSettingsContext.Provider value={{
      settings,
      logo,
      hero,
      authBg,
      useCases,
      rawUseCases,
      marqueeSpeed1,
      marqueeSpeed2,
      marqueeDir1,
      marqueeDir2,
      highlights,
      rawHighlights,
      highlightsTitle,
      rawHighlightsTitle,
      infos,
      rawInfos,
      infosTitle,
      rawInfosTitle,
      heroOverlayOpacity,
      refresh,
    }}>
      {children}
    </SiteSettingsContext.Provider>
  );
}

export function useSiteSettings() {
  return useContext(SiteSettingsContext);
}
